import { Calendar } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { blogLocaleFor, formatBlogDate, type BlogPostSummary } from '@/lib/blog'
import { BlogTagChip } from './BlogTagChip'

interface BlogCardProps {
  post: BlogPostSummary
}

export function BlogCard({ post }: BlogCardProps) {
  const { t } = useTranslation()
  const dateLabel = formatBlogDate(post.date, blogLocaleFor(post.language))

  return (
    <article className="group bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden hover:shadow-md hover:border-slate-300 transition-all">
      {post.cover && (
        <Link to={`/blog/${post.slug}`} className="block aspect-[16/9] overflow-hidden bg-slate-100">
          <img
            src={post.cover}
            alt=""
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
          />
        </Link>
      )}

      <div className="p-6 flex flex-col gap-3">
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <Calendar className="w-3.5 h-3.5" />
          <time dateTime={post.date}>{dateLabel}</time>
          <span aria-hidden="true">·</span>
          <span>{post.author}</span>
        </div>

        <h2 className="text-xl font-bold text-slate-900 leading-snug">
          <Link
            to={`/blog/${post.slug}`}
            className="hover:text-[#237CCA] transition-colors"
          >
            {post.title}
          </Link>
        </h2>

        {post.description && (
          <p className="text-sm text-slate-600 leading-relaxed">{post.description}</p>
        )}

        <div className="flex flex-wrap gap-2 pt-1">
          {post.tags.map((tag) => (
            <BlogTagChip key={tag} tag={tag} />
          ))}
        </div>

        <Link
          to={`/blog/${post.slug}`}
          className="mt-2 inline-flex items-center text-sm font-semibold text-[#237CCA] hover:text-[#1a5f9f] transition-colors"
        >
          {t('blog.readMore')} →
        </Link>
      </div>
    </article>
  )
}
